import React from 'react';
import { Outlet, NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { useTenant } from '../contexts/TenantContext';
import { Button } from './ui/button';
import { ScrollArea } from './ui/scroll-area';
import { Badge } from './ui/badge';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from './ui/tooltip';
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from './ui/breadcrumb';
import NotificheDropdown from './NotificheDropdown';
import GlobalSearch from './GlobalSearch';
import QuickActions from './QuickActions';
import MobileBottomNav from './MobileBottomNav';
import { 
  LayoutDashboard, 
  Users, 
  Printer, 
  Ticket, 
  FileText, 
  Settings, 
  LogOut,
  Sun,
  Moon,
  Menu,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  CalendarClock,
  CalendarDays,
  UserCog,
  Package,
  AlertTriangle,
  RefreshCw,
  Database,
  Tag,
  BarChart3,
  Building2,
  Search,
  Home,
  PieChart,
  Landmark,
  Megaphone,
  MailCheck,
  CreditCard,
  FileSignature
} from 'lucide-react';
import { useState, useEffect } from 'react';
import axios from 'axios';
import { cn } from '../lib/utils';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const navGroups = [
  {
    id: 'home',
    label: 'Home',
    icon: LayoutDashboard,
    items: [
      { to: '/', icon: LayoutDashboard, label: 'Dashboard', roles: ['admin', 'tecnico', 'operatore'] },
    ],
  },
  {
    id: 'anagrafica',
    label: 'Anagrafica',
    icon: Users,
    items: [
      { to: '/clienti', icon: Users, label: 'Clienti', roles: ['admin', 'tecnico', 'operatore', 'commerciale'] },
      { to: '/impianti', icon: Printer, label: 'Impianti', roles: ['admin', 'tecnico', 'operatore'] },
      { to: '/avvisi-clienti', icon: Megaphone, label: 'Avvisi Email', roles: ['admin'] },
      { to: '/log-email', icon: MailCheck, label: 'Log Email', roles: ['admin'] },
    ],
  },
  {
    id: 'operativo',
    label: 'Operativo',
    icon: Ticket,
    items: [
      { to: '/tickets', icon: Ticket, label: 'Tickets', roles: ['admin', 'tecnico', 'operatore', 'commerciale'] },
      { to: '/calendario', icon: CalendarDays, label: 'Calendario', roles: ['admin', 'tecnico', 'operatore', 'commerciale'] },
      { to: '/documenti', icon: FileText, label: 'Documenti', roles: ['admin', 'tecnico', 'operatore', 'commerciale'] },
      { to: '/contratti', icon: FileSignature, label: 'Contratti', roles: ['admin', 'operatore'] },
      { to: '/contratti-gestionali', icon: CreditCard, label: 'Contratti Gestionali', roles: ['admin', 'operatore'] },
      { to: '/fatturazione', icon: Landmark, label: 'Fatturazione SdI', roles: ['admin'] },
    ],
  },
  {
    id: 'magazzino',
    label: 'Magazzino & Listino',
    icon: Package,
    items: [
      { to: '/magazzino', icon: Package, label: 'Magazzino', roles: ['admin'], featureKey: 'magazzino' },
      { to: '/listino-prezzi', icon: Tag, label: 'Listino', roles: ['admin', 'operatore'], featureKey: 'listino_prezzi' },
    ],
  },
  {
    id: 'scadenze',
    label: 'Scadenze',
    icon: AlertTriangle,
    items: [
      { to: '/verifiche-scadenza', icon: AlertTriangle, label: 'Verifiche RT', roles: ['admin', 'tecnico', 'operatore'] },
    ],
  },
  {
    id: 'analytics',
    label: 'Analytics',
    icon: BarChart3,
    items: [
      { to: '/statistiche', icon: BarChart3, label: 'Statistiche', roles: ['admin'], featureKey: 'statistiche' },
      { to: '/reports', icon: PieChart, label: 'Reports', roles: ['admin'] },
    ],
  },
  {
    id: 'sistema',
    label: 'Sistema',
    icon: Settings,
    items: [
      { to: '/utenti', icon: UserCog, label: 'Utenti', roles: ['admin'] },
      { to: '/impostazioni', icon: Settings, label: 'Impostazioni', roles: ['admin'] },
      { to: '/backup', icon: Database, label: 'Backup', roles: ['admin'], featureKey: 'backup' },
    ],
  },
];

const superAdminItem = { to: '/super-admin', icon: Building2, label: 'Super Admin', roles: ['admin'], section: 'super' };

const menuFeatureMap = {
  '/magazzino': 'magazzino',
  '/statistiche': 'statistiche',
  '/backup': 'backup',
  '/listino-prezzi': 'listino_prezzi',
};

// Breadcrumb mapping
const breadcrumbMap = {
  '/': { label: 'Dashboard', icon: Home },
  '/clienti': { label: 'Clienti', icon: Users },
  '/clienti/nuovo': { label: 'Nuovo Cliente', parent: '/clienti' },
  '/impianti': { label: 'Impianti', icon: Printer },
  '/tickets': { label: 'Tickets', icon: Ticket },
  '/calendario': { label: 'Calendario', icon: CalendarDays },
  '/documenti': { label: 'Documenti', icon: FileText },
  '/contratti': { label: 'Contratti', icon: FileSignature },
  '/fatturazione': { label: 'Fatturazione Elettronica', icon: Landmark },
  '/magazzino': { label: 'Magazzino', icon: Package },
  '/verifiche-scadenza': { label: 'Verifiche Scadenza', icon: AlertTriangle },
  '/statistiche': { label: 'Statistiche', icon: BarChart3 },
  '/utenti': { label: 'Utenti', icon: UserCog },
  '/impostazioni': { label: 'Impostazioni', icon: Settings },
  '/backup': { label: 'Backup', icon: Database },
  '/listino-prezzi': { label: 'Listino Prezzi', icon: Tag },
  '/scadenze': { label: 'Scadenze', icon: AlertTriangle },
  '/analytics': { label: 'Analytics', icon: BarChart3 },
  '/tecnico': { label: 'Modalità Tecnico', icon: UserCog },
  '/super-admin': { label: 'Super Admin', icon: Building2 },
};

export const Layout = () => {
  const { user, logout } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const { hasFeature, getPianoName } = useTenant();
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);
  const [impostazioni, setImpostazioni] = useState(null);
  const [searchOpen, setSearchOpen] = useState(false);

  // Il commerciale accede solo a Clienti, Tickets, Documenti (preventivi) e Calendario
  useEffect(() => {
    if (user?.ruolo !== 'commerciale') return;
    const allowed = ['/clienti', '/tickets', '/documenti', '/calendario'];
    const ok = allowed.some((p) => location.pathname === p || location.pathname.startsWith(p + '/'));
    if (!ok) navigate('/clienti', { replace: true });
  }, [user, location.pathname, navigate]);

  useEffect(() => {
    const fetchImpostazioni = async () => {
      try {
        const response = await axios.get(`${API}/impostazioni`);
        setImpostazioni(response.data);
      } catch (error) {
        console.error('Error fetching settings:', error);
      }
    };
    fetchImpostazioni();
  }, []);

  // Load sidebar state from localStorage
  useEffect(() => {
    const saved = localStorage.getItem('sidebarCollapsed');
    if (saved !== null) {
      setSidebarCollapsed(JSON.parse(saved));
    }
  }, []);

  const toggleSidebar = () => {
    const newState = !sidebarCollapsed;
    setSidebarCollapsed(newState);
    localStorage.setItem('sidebarCollapsed', JSON.stringify(newState));
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const userIsSuperAdmin = user?.ruolo === 'admin' && !user?.tenant_id;

  // Filtra ogni gruppo in base ai ruoli/feature, rimuovi gruppi vuoti
  const filteredGroups = navGroups
    .map(g => ({
      ...g,
      items: g.items.filter(it => {
        if (!it.roles.includes(user?.ruolo)) return false;
        if (it.featureKey && !hasFeature(it.featureKey)) return false;
        return true;
      })
    }))
    .filter(g => g.items.length > 0);

  // Gruppi espandibili: aperto di default se contiene la route corrente
  const [openGroups, setOpenGroups] = useState(() => {
    try {
      const saved = localStorage.getItem('navOpenGroups');
      if (saved) return JSON.parse(saved);
    } catch (e) { console.debug('Preferenze menu non lette:', e); }
    return {};
  });

  useEffect(() => {
    // Auto-apre il gruppo della pagina corrente (navGroups è una costante di modulo)
    const activeGroup = navGroups.find(g => g.items.some(it => location.pathname === it.to || (it.to !== '/' && location.pathname.startsWith(it.to))));
    if (activeGroup) {
      setOpenGroups(prev => (prev[activeGroup.id] ? prev : { ...prev, [activeGroup.id]: true }));
    }
  }, [location.pathname]);

  const toggleGroup = (gid) => {
    setOpenGroups(prev => {
      const newState = { ...prev, [gid]: !prev[gid] };
      try { localStorage.setItem('navOpenGroups', JSON.stringify(newState)); } catch (e) { console.debug('Preferenze menu non salvate:', e); }
      return newState;
    });
  };

  // Build breadcrumbs
  const buildBreadcrumbs = () => {
    const pathParts = location.pathname.split('/').filter(Boolean);
    const breadcrumbs = [{ path: '/', label: 'Home', icon: Home }];
    
    let currentPath = '';
    for (const part of pathParts) {
      currentPath += `/${part}`;
      const mapping = breadcrumbMap[currentPath];
      
      if (mapping) {
        breadcrumbs.push({
          path: currentPath,
          label: mapping.label,
          icon: mapping.icon
        });
      } else {
        // Dynamic routes (like /clienti/:id)
        const parentPath = currentPath.split('/').slice(0, -1).join('/');
        const parentMapping = breadcrumbMap[parentPath];
        if (parentMapping) {
          breadcrumbs.push({
            path: currentPath,
            label: `Dettaglio`,
            icon: parentMapping.icon
          });
        }
      }
    }
    
    return breadcrumbs;
  };

  const breadcrumbs = buildBreadcrumbs();

  return (
    <TooltipProvider delayDuration={0}>
      <div className="min-h-screen bg-background">
        {/* Global Search */}
        <GlobalSearch open={searchOpen} onOpenChange={setSearchOpen} />

        {/* Header */}
        <header className="h-14 border-b border-border flex items-center px-4 bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 fixed top-0 left-0 right-0 z-50">
          {/* Mobile menu button */}
          <button 
            onClick={() => setMobileSidebarOpen(!mobileSidebarOpen)}
            className="md:hidden p-2 hover:bg-accent transition-colors rounded-md"
            data-testid="mobile-menu-toggle"
          >
            <Menu className="h-5 w-5" />
          </button>
          
          {/* Logo */}
          <div className="flex items-center gap-3 ml-2 md:ml-0">
            {impostazioni?.logo_base64 ? (
              <img 
                src={`data:image/png;base64,${impostazioni.logo_base64}`} 
                alt="Logo" 
                className="h-8 w-auto"
                data-testid="header-logo"
              />
            ) : (
              <h1 className="text-xl font-black tracking-tight text-cyan-500">
                {impostazioni?.nome_azienda || 'VELTA'}
              </h1>
            )}
          </div>

          {/* Search Button */}
          <Button
            variant="outline"
            className="ml-4 hidden md:flex items-center gap-2 text-muted-foreground hover:text-foreground h-9 px-3 rounded-md border-border/50"
            onClick={() => setSearchOpen(true)}
            data-testid="global-search-button"
          >
            <Search className="h-4 w-4" />
            <span className="text-sm">Cerca...</span>
            <kbd className="ml-2 pointer-events-none hidden sm:inline-flex h-5 select-none items-center gap-1 rounded border bg-muted px-1.5 font-mono text-[10px] font-medium text-muted-foreground">
              <span className="text-xs">Ctrl</span>K
            </kbd>
          </Button>

          {/* Breadcrumb */}
          <div className="hidden lg:flex items-center ml-6">
            <Breadcrumb>
              <BreadcrumbList>
                {breadcrumbs.map((crumb, index) => (
                  <React.Fragment key={crumb.path}>
                    <BreadcrumbItem>
                      {index < breadcrumbs.length - 1 ? (
                        <BreadcrumbLink 
                          href={crumb.path}
                          onClick={(e) => { e.preventDefault(); navigate(crumb.path); }}
                          className="flex items-center gap-1 text-xs hover:text-cyan-500"
                        >
                          {index === 0 && <Home className="h-3 w-3" />}
                          {index > 0 && crumb.label}
                        </BreadcrumbLink>
                      ) : (
                        <BreadcrumbPage className="text-xs font-medium">
                          {crumb.label}
                        </BreadcrumbPage>
                      )}
                    </BreadcrumbItem>
                    {index < breadcrumbs.length - 1 && <BreadcrumbSeparator />}
                  </React.Fragment>
                ))}
              </BreadcrumbList>
            </Breadcrumb>
          </div>
          
          {/* Right side */}
          <div className="ml-auto flex items-center gap-2">
            {/* Mobile search */}
            <Button 
              variant="ghost" 
              size="icon" 
              onClick={() => setSearchOpen(true)}
              className="md:hidden rounded-md h-9 w-9"
            >
              <Search className="h-4 w-4" />
            </Button>

            <NotificheDropdown />
            
            <QuickActions />

            <Button 
              variant="ghost" 
              size="icon" 
              onClick={toggleTheme}
              className="rounded-md h-9 w-9"
              data-testid="theme-toggle"
            >
              {theme === 'light' ? <Moon className="h-4 w-4" /> : <Sun className="h-4 w-4" />}
            </Button>
            
            <div className="hidden md:flex items-center gap-2 text-sm border-l border-border/50 pl-3 ml-1">
              <span className="text-muted-foreground text-xs">{user?.nome}</span>
              <Badge
                variant="outline"
                className="rounded text-[10px] px-1.5 py-0 uppercase border-cyan-600 text-cyan-600"
                data-testid="utente-ruolo"
              >
                {user?.ruolo}
              </Badge>
              {user?.ruolo === 'admin' && !userIsSuperAdmin && (
                <Badge 
                  variant="outline" 
                  className={cn(
                    "rounded text-[10px] px-1.5 py-0",
                    getPianoName() === 'Enterprise' ? 'border-purple-500 text-purple-500' :
                    getPianoName() === 'Pro' ? 'border-blue-500 text-blue-500' :
                    'border-gray-500 text-gray-500'
                  )}
                >
                  {getPianoName()}
                </Badge>
              )}
            </div>
            
            <Button 
              variant="ghost" 
              size="icon" 
              onClick={handleLogout}
              className="rounded-md h-9 w-9"
              data-testid="logout-button"
            >
              <LogOut className="h-4 w-4" />
            </Button>
          </div>
        </header>

        <div className="flex pt-14">
          {/* Sidebar */}
          <aside className={cn(
            "fixed md:sticky top-14 left-0 z-40 h-[calc(100vh-3.5rem)] border-r border-border/30 bg-slate-900 dark:bg-slate-950",
            "transform transition-all duration-300 ease-in-out",
            sidebarCollapsed ? "w-16" : "w-56",
            mobileSidebarOpen ? "translate-x-0" : "-translate-x-full md:translate-x-0"
          )}>
            {/* Collapse Toggle */}
            <Button
              variant="ghost"
              size="icon"
              onClick={toggleSidebar}
              className="absolute -right-3 top-6 h-6 w-6 rounded-full border border-border bg-background shadow-md hidden md:flex z-50"
              data-testid="sidebar-toggle"
            >
              {sidebarCollapsed ? (
                <ChevronRight className="h-3 w-3" />
              ) : (
                <ChevronLeft className="h-3 w-3" />
              )}
            </Button>

            <ScrollArea className="h-full py-4">
              <nav className="space-y-1 px-2">
                {/* Super Admin link in cima (solo super admin) */}
                {userIsSuperAdmin && (
                  <NavLink
                    to="/super-admin"
                    onClick={() => setMobileSidebarOpen(false)}
                    className={cn(
                      "flex items-center gap-3 px-3 py-2.5 text-sm rounded-md transition-all mb-3",
                      "bg-amber-500/10 text-amber-500 hover:bg-amber-500/20",
                      location.pathname.startsWith('/super-admin') && "ring-1 ring-amber-500",
                      sidebarCollapsed && "justify-center px-0"
                    )}
                    data-testid="nav-super-admin"
                  >
                    <Building2 className="h-5 w-5 flex-shrink-0" />
                    {!sidebarCollapsed && <span className="truncate font-medium">Super Admin</span>}
                  </NavLink>
                )}

                {filteredGroups.map((group) => {
                  const GroupIcon = group.icon;
                  const isOpen = openGroups[group.id];
                  const isSingleton = group.items.length === 1;
                  const hasActive = group.items.some(it => location.pathname === it.to || (it.to !== '/' && location.pathname.startsWith(it.to)));

                  // Caso "singolo" (es. Dashboard): renderizza come voce diretta senza espandere
                  if (isSingleton && group.id === 'home') {
                    const it = group.items[0];
                    const ItIcon = it.icon;
                    const isActive = location.pathname === it.to || (it.to !== '/' && location.pathname.startsWith(it.to));
                    const link = (
                      <NavLink
                        to={it.to}
                        onClick={() => setMobileSidebarOpen(false)}
                        className={cn(
                          "flex items-center gap-3 px-3 py-2.5 text-sm rounded-md transition-all",
                          "text-slate-400 hover:text-white hover:bg-slate-800",
                          isActive && "bg-cyan-500/10 text-cyan-400 border-l-2 border-cyan-500 -ml-[2px] pl-[14px]",
                          sidebarCollapsed && "justify-center px-0"
                        )}
                        data-testid={`nav-${it.label.toLowerCase().replace(/\s+/g, '-')}`}
                      >
                        <ItIcon className={cn("h-5 w-5 flex-shrink-0", isActive && "text-cyan-400")} />
                        {!sidebarCollapsed && <span className="truncate">{it.label}</span>}
                      </NavLink>
                    );
                    return sidebarCollapsed ? (
                      <Tooltip key={group.id}>
                        <TooltipTrigger asChild>{link}</TooltipTrigger>
                        <TooltipContent side="right">{it.label}</TooltipContent>
                      </Tooltip>
                    ) : <div key={group.id}>{link}</div>;
                  }

                  // Sidebar collassata: mostra solo icone gruppo con tooltip + link primo item del gruppo
                  if (sidebarCollapsed) {
                    return (
                      <div key={group.id} className="space-y-1">
                        {group.items.map(it => {
                          const ItIcon = it.icon;
                          const isActive = location.pathname === it.to || (it.to !== '/' && location.pathname.startsWith(it.to));
                          return (
                            <Tooltip key={it.to}>
                              <TooltipTrigger asChild>
                                <NavLink
                                  to={it.to}
                                  onClick={() => setMobileSidebarOpen(false)}
                                  className={cn(
                                    "flex items-center justify-center gap-3 px-0 py-2.5 text-sm rounded-md transition-all",
                                    "text-slate-400 hover:text-white hover:bg-slate-800",
                                    isActive && "bg-cyan-500/10 text-cyan-400"
                                  )}
                                  data-testid={`nav-${it.label.toLowerCase().replace(/\s+/g, '-')}`}
                                >
                                  <ItIcon className={cn("h-5 w-5 flex-shrink-0", isActive && "text-cyan-400")} />
                                </NavLink>
                              </TooltipTrigger>
                              <TooltipContent side="right">{it.label}</TooltipContent>
                            </Tooltip>
                          );
                        })}
                      </div>
                    );
                  }

                  return (
                    <div key={group.id} className="mb-1">
                      <button
                        type="button"
                        onClick={() => toggleGroup(group.id)}
                        className={cn(
                          "w-full flex items-center justify-between gap-3 px-3 py-2.5 text-sm rounded-md transition-all",
                          hasActive 
                            ? "text-cyan-400 bg-slate-800/50"
                            : "text-slate-300 hover:text-white hover:bg-slate-800"
                        )}
                        data-testid={`nav-group-${group.id}`}
                      >
                        <span className="flex items-center gap-3">
                          <GroupIcon className={cn("h-4.5 w-4.5 flex-shrink-0", hasActive && "text-cyan-400")} />
                          <span className="font-medium tracking-tight">{group.label}</span>
                        </span>
                        <ChevronDown className={cn(
                          "h-4 w-4 transition-transform duration-200",
                          isOpen && "rotate-180"
                        )} />
                      </button>
                      {isOpen && (
                        <div className="mt-1 ml-2 pl-3 border-l border-slate-800 space-y-0.5">
                          {group.items.map(it => {
                            const ItIcon = it.icon;
                            const isActive = location.pathname === it.to || (it.to !== '/' && location.pathname.startsWith(it.to));
                            return (
                              <NavLink
                                key={it.to}
                                to={it.to}
                                onClick={() => setMobileSidebarOpen(false)}
                                className={cn(
                                  "flex items-center gap-3 px-3 py-2 text-[13px] rounded-md transition-all",
                                  "text-slate-400 hover:text-white hover:bg-slate-800",
                                  isActive && "bg-cyan-500/10 text-cyan-400 border-l-2 border-cyan-500 -ml-[2px] pl-[10px]"
                                )}
                                data-testid={`nav-${it.label.toLowerCase().replace(/\s+/g, '-')}`}
                              >
                                <ItIcon className={cn("h-4 w-4 flex-shrink-0", isActive && "text-cyan-400")} />
                                <span className="truncate">{it.label}</span>
                              </NavLink>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                })}
              </nav>
            </ScrollArea>
          </aside>

          {/* Mobile Overlay */}
          {mobileSidebarOpen && (
            <div 
              className="fixed inset-0 bg-background/80 backdrop-blur-sm z-30 md:hidden"
              onClick={() => setMobileSidebarOpen(false)}
            />
          )}

          {/* Main Content */}
          <main className={cn(
            "flex-1 min-h-[calc(100vh-3.5rem)] overflow-auto transition-all duration-300",
            sidebarCollapsed ? "md:ml-0" : "md:ml-0"
          )}>
            <div className="p-4 md:p-6 lg:p-8 pb-20 md:pb-8 max-w-7xl mx-auto">
              <Outlet />
            </div>
          </main>
        </div>

        {/* Mobile Bottom Navigation */}
        <MobileBottomNav onMenuClick={() => setMobileSidebarOpen(true)} />
      </div>
    </TooltipProvider>
  );
};

export default Layout;
